import * as React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Alert, Box, Button, SpaceBetween, FormField, Textarea, Container } from '@amzn/awsui-components-react';
import updateDeviceComponentStatusHandler, {
    UpdateDeviceComponentStatusRequest,
} from '../../services/updateDeviceComponentStatusHandler';
import { ComponentOnboardingStatus } from '../../services/getDeviceComponentStatusHandler';
import { OnboardingStatus } from '../../constants/onboardingStatus';
import { delayedApiCall } from '../../utils/delayedApiCall';
import ComponentInstallationDetails from './componentInstallationDetails';
import { ComponentType } from './componentType';

interface InstallationProgressProps {
    deviceId: string;
    vin: string;
    componentType: ComponentType;
    serialNumber: string;
    notes: string;
    onInstallationComplete: (notes: string) => void;
    onCancel: () => void;
    existingComponentInfo?: ComponentOnboardingStatus;
}

const InstallationProgress: React.FC<InstallationProgressProps> = ({
    deviceId,
    vin,
    componentType,
    serialNumber,
    notes,
    onInstallationComplete,
    onCancel,
    existingComponentInfo,
}) => {
    const [currentNotes, setCurrentNotes] = useState(existingComponentInfo?.errorMessage || notes);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const hasUpdatedToInProgress = useRef(false);

    useEffect(() => {
        // Update status to IN_PROGRESS when this page is loaded
        if (!hasUpdatedToInProgress.current) {
            hasUpdatedToInProgress.current = true;
            setIsUpdatingStatus(true);

            const updateRequest: UpdateDeviceComponentStatusRequest = {
                deviceId: deviceId,
                componentId: serialNumber,
                vin: vin,
                componentType: componentType.value,
                status: OnboardingStatus.IN_PROGRESS,
                notes: 'Component installation in progress',
            };

            delayedApiCall((callback) => {
                updateDeviceComponentStatusHandler(updateRequest, callback);
            }).then(({ success, result }) => {
                setIsUpdatingStatus(false);
                if (!success) {
                    console.error('Failed to update component status to IN_PROGRESS:', result);
                }
            });
        }
    }, []);

    const handleCancel = useCallback(() => {
        setIsCancelling(true);

        const updateRequest: UpdateDeviceComponentStatusRequest = {
            deviceId: deviceId,
            componentId: serialNumber,
            vin: vin,
            componentType: componentType.value,
            status: OnboardingStatus.CANCELLED,
            notes: 'Component installation cancelled by user',
        };

        delayedApiCall((callback) => {
            updateDeviceComponentStatusHandler(updateRequest, callback);
        }).then(({ success, result }) => {
            if (!success) {
                console.error('Failed to update component status to CANCELLED:', result);
            }
            onCancel();
        });
    }, [onCancel]);

    const handleInstallationComplete = useCallback(() => {
        onInstallationComplete(currentNotes);
    }, [onInstallationComplete, currentNotes]);

    const isExistingComponent = !!existingComponentInfo;
    const pageTitle = isExistingComponent ? 'Continue Component Installation' : 'Installing Component';

    if (isUpdatingStatus) {
        return (
            <Container>
                <SpaceBetween size="l">
                    <Box variant="h2">{pageTitle}</Box>

                    <Alert type="info" header="Loading the Installation Page">
                        Loading the installation page for {componentType.label}... This could take a few seconds.
                    </Alert>
                </SpaceBetween>
            </Container>
        );
    }

    if (isCancelling) {
        return (
            <Container>
                <SpaceBetween size="l">
                    <Box variant="h2">{pageTitle}</Box>

                    <Alert type="info" header="Cancelling Installation">
                        Cancelling the installation for {componentType.label}, please keep this page open.
                    </Alert>
                </SpaceBetween>
            </Container>
        );
    }

    return (
        <Container>
            <SpaceBetween size="l">
                <Box variant="h2">{pageTitle}</Box>

                <Alert type="info" header="Installation in Progress">
                    {isExistingComponent
                        ? 'There is an existing component installation in progress. Please proceed with the current installation.'
                        : 'Please keep this page open during the installation process. Do not navigate away or close the browser.'}
                </Alert>

                <ComponentInstallationDetails
                    deviceId={deviceId}
                    vin={vin}
                    componentType={componentType}
                    serialNumber={existingComponentInfo?.componentId || serialNumber}
                    notes={existingComponentInfo?.errorMessage || notes}
                    title="Installation Details"
                />

                <SpaceBetween size="s">
                    <Box variant="h3">Installation Actions</Box>
                    <Alert type="info" header="Manual Installation Required">
                        Please perform the {componentType.label}
                        component installation manually on the device. Once the installation is complete, click the{' '}
                        <strong>"Installation Complete"</strong> button below.
                    </Alert>

                    <FormField
                        label="Installation Notes (Optional)"
                        description="Enter any additional notes about the installation"
                        stretch={true}
                    >
                        <Textarea
                            value={currentNotes}
                            onChange={({ detail }) => setCurrentNotes(detail.value)}
                            placeholder="Enter installation notes here..."
                            rows={4}
                        />
                    </FormField>

                    <SpaceBetween size="s" direction="horizontal">
                        <Button variant="primary" onClick={handleInstallationComplete}>
                            Installation Complete
                        </Button>
                        <Button variant="link" onClick={handleCancel}>
                            Cancel Installation
                        </Button>
                    </SpaceBetween>
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
};

export default InstallationProgress;
